import { Navigate, useParams } from 'react-router-dom';
import {
  LEGAL_AGREEMENTS,
  type LegalAgreementId,
} from '../data/legalAgreements';

const AGREEMENT_IDS: LegalAgreementId[] = [
  'factory-technical-service-purchase-agreement',
  'member-service-agreement',
  'payment-authorization-agreement',
];
const BRACKET_HEADING_RE = /^【.+】$/;
const CHINESE_SECTION_RE = /^[一二三四五六七八九十]+、/;
const NUMBERED_CLAUSE_RE = /^\d+(?:\.\d+)+\s/;
const IMPORTANT_CLAUSE_RE = /^重要提示：/;

function isLegalAgreementId(value: string | undefined): value is LegalAgreementId {
  return AGREEMENT_IDS.includes(value as LegalAgreementId);
}

function renderClause(text: string, index: number) {
  if (BRACKET_HEADING_RE.test(text) || CHINESE_SECTION_RE.test(text)) {
    return (
      <h2 key={`${index}-${text}`} className="mt-8 text-xl font-bold leading-8 text-stone-950">
        {text}
      </h2>
    );
  }
  if (NUMBERED_CLAUSE_RE.test(text)) {
    return (
      <h3 key={`${index}-${text}`} className="mt-5 text-base font-semibold leading-7 text-stone-900">
        {text}
      </h3>
    );
  }
  if (IMPORTANT_CLAUSE_RE.test(text)) {
    return (
      <p
        key={`${index}-${text}`}
        className="mt-5 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-base font-semibold leading-8 text-stone-950"
      >
        {text}
      </p>
    );
  }
  return (
    <p key={`${index}-${text}`} className="mt-4 text-base leading-8 text-stone-700">
      {text}
    </p>
  );
}

export default function LegalAgreementPage() {
  const { agreementId } = useParams();
  if (!isLegalAgreementId(agreementId)) {
    return <Navigate to="/" replace />;
  }

  const agreement = LEGAL_AGREEMENTS[agreementId];

  return (
    <main className="min-h-screen bg-white px-4 py-10 sm:px-6 lg:px-8">
      <article
        data-testid={`legal-agreement-${agreementId}`}
        className="mx-auto max-w-4xl"
      >
        <p className="text-xs font-semibold uppercase text-stone-500">
          Houmao Legal
        </p>
        <h1 className="mt-3 text-3xl font-bold leading-tight text-stone-950 sm:text-4xl">
          {agreement.title}
        </h1>
        {agreement.meta.length > 0 ? (
          <div className="mt-5 space-y-1 border-l-4 border-amber-400 pl-4 text-sm leading-6 text-stone-600">
            {agreement.meta.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </div>
        ) : null}
        <div className="mt-8">{agreement.body.map(renderClause)}</div>
      </article>
    </main>
  );
}
